import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export function ResetPasswordPage() {
  const navigate = useNavigate()

  const [ready, setReady] = useState(false)      // recovery session detected?
  const [checking, setChecking] = useState(true) // still verifying the link?
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  // When the user arrives from the email link, Supabase establishes a
  // recovery session. We confirm that session exists before showing the form,
  // so a random visitor to /reset-password can't set a password.
  useEffect(() => {
    let active = true

    // Supabase fires PASSWORD_RECOVERY when the recovery link is processed.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (!active) return
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true)
        setChecking(false)
      }
    })

    // Also check immediately — if the event already fired before this mounted,
    // there will be an active session from the recovery link.
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      if (data?.session) {
        setReady(true)
      }
      setChecking(false)
    })

    return () => {
      active = false
      sub?.subscription?.unsubscribe?.()
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      const { error: err } = await supabase.auth.updateUser({ password })
      if (err) throw err
      setDone(true)
      // Brief pause so the user sees the success state, then send to dashboard.
      setTimeout(() => navigate('/dashboard', { replace: true }), 1800)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const cardStyle = {
    width: '100%', maxWidth: '400px', background: '#FFFFFF',
    borderRadius: '12px', padding: '2rem', boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  }
  const wrapStyle = {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '2rem 1rem', background: '#F7FAFC',
  }
  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0',
    borderRadius: '6px', fontSize: '15px',
  }
  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px',
  }

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <div style={{
          width: '40px', height: '40px', background: '#2C7A7B', borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF',
          fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, marginBottom: '1rem',
        }}>K</div>

        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif", fontSize: '24px',
          fontWeight: 500, color: '#1A202C', marginBottom: '1rem',
        }}>Set a new password</h1>

        {/* State 1: still verifying the recovery link */}
        {checking && (
          <p style={{ color: '#718096', fontSize: '14px' }}>Verifying your reset link…</p>
        )}

        {/* State 2: no valid recovery session */}
        {!checking && !ready && !done && (
          <div>
            <div style={{ padding: '10px 12px', background: '#FCF5E8', color: '#8b5000', borderRadius: '6px', fontSize: '13px', lineHeight: 1.6, marginBottom: '1.25rem' }}>
              This password reset link is invalid or has expired. Reset links can only be used once and expire after a short time.
            </div>
            <Link to="/forgot-password" style={{ color: '#2C7A7B', textDecoration: 'none', fontWeight: 500, fontSize: '14px' }}>
              Request a new reset link
            </Link>
          </div>
        )}

        {/* State 3: success */}
        {done && (
          <div style={{ padding: '12px 14px', background: '#E1F5EE', color: '#005440', borderRadius: '6px', fontSize: '14px', lineHeight: 1.6 }}>
            Your password has been updated. Taking you to your dashboard…
          </div>
        )}

        {/* State 4: the form */}
        {!checking && ready && !done && (
          <form onSubmit={handleSubmit}>
            <p style={{ color: '#4A5568', lineHeight: 1.6, marginBottom: '1.25rem', fontSize: '14px' }}>
              Choose a new password for your account.
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="new-password" style={labelStyle}>New password</label>
              <input
                id="new-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required
                minLength={8} autoComplete="new-password" style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="confirm-password" style={labelStyle}>Confirm new password</label>
              <input
                id="confirm-password" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required
                minLength={8} autoComplete="new-password" style={inputStyle}
              />
            </div>

            {error && (
              <div style={{ padding: '10px 12px', background: '#FAECE7', color: '#993C1D', borderRadius: '6px', fontSize: '13px', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              style={{
                width: '100%', padding: '11px', background: '#2C7A7B', color: '#FFFFFF',
                border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>

            <p style={{ marginTop: '1rem', fontSize: '14px', color: '#4A5568' }}>
              <Link to="/login" style={{ color: '#2C7A7B', textDecoration: 'none', fontWeight: 500 }}>Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
